import { deepmerge } from '@mui/utils';
import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { pieChartSliceColors } from '../../../../components/Metrics/PieChartCard';
import theme from '../../../../theme';
import { formatNumber, formatPercent, ratio } from '../../HatUtils';
import { HatMetricChannelAttributionData } from '../dashboard/HatDbSectionChannelAttribution';
import { HatDbChartCard } from './HatDbChartCard';
import HatDbVisualSkeletonPieChart from './HatDbVisualSkeletonPieChart';

export function apexDonutChartOptionsFactory(options: { totalLabel: string }): ApexOptions {
    return {
        colors: pieChartSliceColors,
        chart: {
            type: 'donut',
            width: '100%',
            redrawOnWindowResize: true,
            redrawOnParentResize: true,
        },
        dataLabels: {
            enabled: false,
        },
        legend: {
            position: 'bottom',
            fontSize: '12px',
            fontFamily: theme.typography.fontFamily,
            fontWeight: 400,
        },
        plotOptions: {
            pie: {
                donut: {
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            offsetY: 20,
                        },
                        value: {
                            show: true,
                            color: theme.palette.text.primary,
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '20px',
                            offsetY: -20,
                            formatter: function (value) {
                                return `${value}%`;
                            },
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: options.totalLabel,
                            color: theme.palette.text.secondary,
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '12px',
                            formatter: function (w) {
                                return formatNumber(
                                    w.globals.seriesTotals.reduce((a: number, b: number) => {
                                        return a + b;
                                    }, 0)
                                );
                            },
                        },
                    },
                },
            },
        },
    };
}

const defaultChartOptions = apexDonutChartOptionsFactory({
    totalLabel: 'Attributed Visits',
});

export function HatDbVisualChannelAttributionPieChartCard(props: {
    currentData: HatMetricChannelAttributionData;
    isPending: boolean;
}) {
    const { currentData } = props;

    const chartOptions: ApexOptions = useMemo(() => {
        const slices = currentData
            .map((channel) => ({
                label: channel.sourceCategory,
                value: channel.adInfluencedCount ?? 0,
            }))
            // Sort slices in descending order.
            .sort((a, b) => b.value - a.value);

        const total = currentData.total?.adInfluencedCount ?? 0;

        return deepmerge(
            defaultChartOptions,
            {
                labels: slices.map(({ label }) => label),
                series: slices.map(({ value }) => value),
                tooltip: {
                    y: {
                        formatter: (value: number) => {
                            return formatPercent(ratio(value, total));
                        },
                    },
                },
            },
            {
                clone: true,
            }
        );
    }, [currentData]);

    const isPending = currentData.total?.count === null;

    return (
        <HatDbChartCard>
            {isPending && <HatDbVisualSkeletonPieChart marginTop="60px" marginBottom="60px" />}

            {!isPending && <ReactApexChart type="donut" options={chartOptions} series={chartOptions.series} />}
        </HatDbChartCard>
    );
}
